import { Button, Descriptions, Input, PageHeader, Table, Typography, Tag } from 'antd';
import React, { CSSProperties, ReactElement, useEffect, useRef, useState } from 'react';
import { chineseDrugLink, diseaseLink, ingredientLink, plantDetailLink, recipeLink } from '../hooks/TableLink';
import { useHistory, useParams } from 'react-router-dom';

import { ColumnProps } from 'antd/lib/table';
import Highlighter from 'react-highlight-words';
import { SearchOutlined } from '@ant-design/icons';
import useFetch from '../hooks/fetcher';
import _ from 'lodash';
const API_SERVICE_BASE_URL = process.env.REACT_APP_API_SERVICE_BASE_URL || '';

const { Item } = Descriptions;
const { Title } = Typography;

const filterIcon = (filtered: any) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />;

const renderHighlightText = (text: any, searchText: string) => (
    <Highlighter
        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
        searchWords={[searchText]}
        autoEscape
        textToHighlight={text.toString()}
    />
);

type targetDetailType = {
    protein: string;
    gene?: string;
    uniprot_entry?: string;
    chembl_id?: string;
    kegg_id?: string;
    pharmacological_action?: string;
    druggable?: string;
};

type relatedIngredientsType = {
    ingredients_id: string;
    compound_name: string;
    molecular_formula: string;
    mw: string;
    xlogp: string;
    hba: string;
    hbd: string;
    tpsa: string;
    rotatable_bond: string;
    lipinski_failure: string;
    atcs: string;
};

type relatedDiseasesType = {
    id: string;
    level4: string;
    level4_description: string;
};

type relatedHerbsType = {
    botanical_name: string;
    chinese_pharmaceutical_name: string;
    pinyin: string;
    chinese_id: string;
    botanical_id: string;
};

type relatedRecipesType = {
    recipes_id: string;
    chinese_character: string;
    pinyin: string;
    english_name: string;
};

type fetchResultType = {
    target_detail: targetDetailType;
    related_diseases: relatedDiseasesType[];
    related_ingredients: relatedIngredientsType[];
    related_herbs: relatedHerbsType[];
    related_recipes: relatedRecipesType[];
};

const h3Style: CSSProperties = {
    marginTop: '20px',
};
const atcTags = (atcList?: string) => {
    if (_.isNil(atcList)) {
        return 'NA';
    }
    return atcList.split(',').map((item: string, key: number) => {
        return (
            <a
                href={`https://www.whocc.no/atc_ddd_index/?code=+${item}+&showdescription=no`}
                target="_blank"
                rel="noopener noreferrer"
                key={key}
            >
                <Tag color="blue" style={{ cursor: 'pointer', margin: '3px' }}>
                    {item}
                </Tag>
            </a>
        );
    });
};
const PageTarget: React.FC = () => {
    const { get } = useFetch(API_SERVICE_BASE_URL);
    const { name: searchName } = useParams<any>();
    const [result, setResult] = useState({} as fetchResultType);
    const [searchText, setSearchText] = useState('');
    const searchInput = useRef<Input>(null);

    const history = useHistory();
    const goToDiseaseDetail = (keyword: string): void => {
        history.push(`/disease/detail/${keyword}`);
    };
    const goToRecipeDetail = (keyword: string, placeholder: string): void => {
        history.push(`/recipe/detail/${placeholder}/${keyword}`);
    };

    const handleSearch = (selectedKeys: any, confirm: VoidFunction, dataIndex: string): void => {
        setSearchText(selectedKeys[0]);
        confirm();
    };

    const handleReset = (clearFilters: any) => {
        setSearchText('');
        clearFilters();
    };

    const filterDropdown = (
        setSelectedKeys: any,
        selectedKeys: any,
        confirm: any,
        clearFilters: any,
        dataIndex: string,
    ) => (
        <div style={{ padding: 8 }}>
            <Input
                ref={searchInput}
                placeholder={`Search compound_name`}
                value={selectedKeys[0]}
                onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                style={{ width: 188, marginBottom: 8, display: 'block' }}
            />
            <Button
                type="primary"
                onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                icon={<SearchOutlined />}
                size="small"
                style={{ width: 90, marginRight: 8 }}
            >
                Search
            </Button>
            <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                Reset
            </Button>
        </div>
    );

    const columnsIngredients: ColumnProps<any>[] = [
        {
            // TODO: Add hot link here: http://bioinf-applied.charite.de/supertcm/ingredient_detail.php?ingredients_id=2188
            title: 'Ingredients ID',
            dataIndex: 'ingredients_id',
            key: 'ingredients_id',
            sorter: (a: relatedIngredientsType, b: relatedIngredientsType): number =>
                parseInt(a.ingredients_id) - parseInt(b.ingredients_id),
            render: ingredientLink,
        },
        {
            title: 'Ingredients Names',
            dataIndex: 'compound_name',
            key: 'compound_name',
            filterIcon: (filtered) => filterIcon(filtered),
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) =>
                filterDropdown(setSelectedKeys, selectedKeys, confirm, clearFilters, 'compound_name'),
            onFilter: (value, record) =>
                record['compound_name'].toString().toLowerCase().includes(value.toString().toLowerCase()),
            onFilterDropdownVisibleChange: (visible) => {
                if (visible) {
                    setTimeout(() => {
                        if (searchInput.current) {
                            searchInput.current.focus();
                        }
                    });
                }
            },
            render: (text: any): ReactElement => renderHighlightText(text, searchText),
        },
        { title: 'Formula', key: 'molecular_formula', dataIndex: 'molecular_formula' },
        {
            title: 'MW',
            key: 'mw',
            dataIndex: 'mw',
        },
        {
            title: 'XLogP',
            key: 'xlogp',
            dataIndex: 'xlogp',
        },
        {
            title: 'HBA',
            key: 'hba',
            dataIndex: 'hba',
        },
        {
            title: 'HBD',
            key: 'hbd',
            dataIndex: 'hbd',
        },
        {
            title: 'TPSA',
            key: 'tpsa',
            dataIndex: 'tpsa',
        },
        {
            title: 'Rotatable Bond',
            key: 'rotatable_bond',
            dataIndex: 'rotatable_bond',
        },
        {
            title: 'Lipinski Failure',
            key: 'lipinski_failure',
            dataIndex: 'lipinski_failure',
        },
        { title: 'ATC Codes', key: 'atcs', dataIndex: 'atcs', render: atcTags },
    ];

    const columnsDiseases = [
        { title: 'Disease Code', dataIndex: 'level4', key: 'level4', render: diseaseLink },
        { title: 'Disease Annotation', dataIndex: 'level4_description', key: 'level4_description' },
    ];
    const columnsHerbs = [
        {
            title: 'Accepted Botanical Name',
            dataIndex: 'botanical_name',
            key: 'botanical_name',
            render: plantDetailLink,
        },
        {
            title: 'Chinese Pharmaceutical Name',
            dataIndex: 'chinese_pharmaceutical_name',
            key: 'chinese_pharmaceutical_name',
            render: chineseDrugLink,
        },
        { title: 'Transliteration', dataIndex: 'pinyin', key: 'pinyin' },
    ];
    const columnsRecipes = [
        {
            title: 'Chinese Pharmaceutical Name ',
            dataIndex: 'chinese_character',
            key: 'chinese_character',
            render: recipeLink,
        },
        { title: 'Transliteration', key: 'pinyin', dataIndex: 'pinyin' },
        { title: 'English Name', dataIndex: 'english_name', key: 'english_name' },
    ];

    useEffect(() => {
        get('/api/target.php', { name: searchName }).then((r) => setResult(r));
    }, []);

    const detail = result.target_detail;
    const relatedDiseases = result.related_diseases;
    const relatedIngredients = result.related_ingredients;
    const relatedHerbs = result.related_herbs;
    const relatedRecipes = result.related_recipes;
    return (
        <div>
            {/* <PageHeader title="Target:" subTitle={detail?.protein} /> */}
            <Title level={3}>Target: {detail?.protein}</Title>
            <Title level={4} style={h3Style}>
                Target Details
            </Title>
            <Descriptions bordered column={{ xxl: 3, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}>
                <Item label="Target Name">{detail?.protein}</Item>
                <Item label="Gene Names">{detail?.gene}</Item>
                <Item label="Uniprot Accession">
                    <a
                        href={'https://www.uniprot.org/uniprot/' + detail?.uniprot_entry}
                        title="bla"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {detail?.uniprot_entry}
                    </a>
                </Item>
                <Item label="KEGG ID">
                    <a
                        href={'https://www.genome.jp/dbget-bin/www_bget?' + detail?.kegg_id}
                        title="bla"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {detail?.kegg_id}
                    </a>
                </Item>
                <Item label="ChEMBL Target ID">
                    <a
                        href={'https://www.ebi.ac.uk/chembl/target_report_card/' + detail?.chembl_id}
                        title="bla"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {detail?.chembl_id}
                    </a>
                </Item>
                <Item label="Pharmacological Action">{detail?.pharmacological_action}</Item>
                <Item label="Druggable">{detail?.druggable}</Item>
            </Descriptions>

            <Title level={4} style={h3Style}>
                Related Ingredients
            </Title>
            <Table bordered rowKey="ingredients_id" dataSource={relatedIngredients} columns={columnsIngredients} />
            <Title level={4} style={h3Style}>
                Related Diseases
            </Title>
            <Table
                bordered
                rowKey="diseases_id"
                dataSource={relatedDiseases}
                columns={columnsDiseases}
                onRow={(record) => ({
                    onClick: () => {
                        goToDiseaseDetail(record.id);
                    },
                    style: { cursor: 'pointer' },
                })}
            />

            <Title level={4} style={h3Style}>
                Related Herbs
            </Title>
            <Table bordered rowKey="herbs_id" dataSource={relatedHerbs} columns={columnsHerbs} />
            <Title level={4} style={h3Style}>
                Related Recipes
            </Title>
            <Table
                bordered
                rowKey="recipes_id"
                dataSource={relatedRecipes}
                columns={columnsRecipes}
                onRow={(record) => ({
                    onClick: (): void => {
                        goToRecipeDetail(record.recipes_id, record.chinese_character);
                    },
                    style: { cursor: 'pointer' },
                })}
            />
        </div>
    );
};

export default PageTarget;
